// @flow
import { merge, isInteger, omitBy } from 'lodash';
import cookies from 'js-cookie';
import { windowWrapper, oidc } from '@clubmed/utils';

import { getQueryParameters } from 'Utils/uri/uriUtils';
import { getTimestamp, isYearsRangeValid } from 'Utils/date/dateUtils';
import { USER_INFO } from 'Utils/ple-utils/cookies';
import { getCxoTravelers, getNeolids } from 'Utils/seller/sellerUtils';
import { unserializeKidsFromQueryParam } from 'Utils/faceting/facetingUtils';
import { getValidationStateFromKids } from 'Utils/faceting/kidsValidationStateUtils';
import {
  QUERY_DEPARTURE_DATE,
  QUERY_DATE_FORMAT,
  QUERY_DURATION,
  QUERY_TRANSPORT,
  QUERY_NUMBER_OF_ADULTS,
  QUERY_KIDS,
  QUERY_CMTA,
} from 'Utils/uri/constants';
import {
  ADULTS_NUMBER_MAX,
  KIDS_NUMBER_MAX,
  DEPARTURE_DATE_MAX_DATE,
  QUERY_QUERY_PARAM,
} from 'Utils/faceting/constants';
import { getFromLocalStorage } from 'Client/local-storage-api';
import { DEFAULT_ADULT_VALUE, DEFAULT_ADULTS_NB } from './trip-information/adults/constants';
import { DEFAULT_KID_VALUE } from './trip-information/kids/constants';
import { getDefaultSelectedMonth } from './calendar/calendar.helper';

export const LOCAL_STORAGE_STATE_KEY = 'state';

function stateFromLocalStorage(locale: string, state: $Shape<ReduxState>): ReduxState {
  const serializedState = getFromLocalStorage(`${LOCAL_STORAGE_STATE_KEY}-${locale}`);
  if (!serializedState) return {};

  const localStorageState = merge(JSON.parse(serializedState), { tripInformation: {} });
  const { kids = [] } = localStorageState.tripInformation;
  return {
    faceting: {
      ...state.faceting,
      kidsValidationState: getValidationStateFromKids(kids),
    },
    tripInformation: {
      kids: kids.map(({ birthdate }) => ({ ...DEFAULT_KID_VALUE, birthdate })),
      adults: kids.length > 0 ? Array(DEFAULT_ADULTS_NB).fill(DEFAULT_ADULT_VALUE) : [],
    },
    seller: localStorageState.seller,
  };
}

export function stateFromCookies(): ReduxState {
  const encodedUserInfo = cookies.get(USER_INFO);
  return { userInfo: encodedUserInfo ? oidc.fromUriEncodedState(encodedUserInfo) : {} };
}

function parseCxoKids(cxoKids) {
  return cxoKids.length <= KIDS_NUMBER_MAX
    ? cxoKids.map(({ neolid, birthdate }) => ({ neolid, birthdate }))
    : [];
}

function parseCxoAdults(cxoAdults) {
  return cxoAdults.length <= ADULTS_NUMBER_MAX
    ? cxoAdults.map(({ neolid, birthdate }) => ({ ...DEFAULT_ADULT_VALUE, neolid, birthdate }))
    : [{}, {}];
}

function parseAdultsQueryParam(adultQueryParam: string) {
  const nbAdults = parseInt(adultQueryParam, 10);
  const hasAdults = isInteger(nbAdults) && nbAdults <= ADULTS_NUMBER_MAX;
  return hasAdults ? Array(nbAdults).fill(DEFAULT_ADULT_VALUE) : [];
}

function stateFromQueryParams(): ReduxState {
  // NOTE: getCxoTravelers() and getNeolids() rely on queryparams
  const neolids = getNeolids();
  const queryParams = getQueryParameters(windowWrapper.getLocation()) || {};
  const { [QUERY_DEPARTURE_DATE]: departureDate } = queryParams;
  const departureDateIsValid = isYearsRangeValid(
    queryParams[QUERY_DEPARTURE_DATE],
    QUERY_DATE_FORMAT,
    DEPARTURE_DATE_MAX_DATE,
  );
  const kids = unserializeKidsFromQueryParam(queryParams[QUERY_KIDS]);
  const adults = parseAdultsQueryParam(queryParams[QUERY_NUMBER_OF_ADULTS]);

  return {
    calendar: {
      selectedMonth: departureDateIsValid ? getDefaultSelectedMonth(departureDate) : undefined,
    },
    // null states are explicitly omitted further
    faceting: {
      query: { label: queryParams[QUERY_QUERY_PARAM] || '' },
      kidsValidationState: getValidationStateFromKids(kids),
    },
    facetingTripInformation: neolids.length > 0 ? null : undefined,
    seller: neolids.length > 0 ? { neolids } : undefined,
    tripCriteria: {
      departureDate: departureDateIsValid
        ? getTimestamp(departureDate, QUERY_DATE_FORMAT)
        : undefined,
      duration: parseInt(queryParams[QUERY_DURATION], 10) || null,
      departureCity: queryParams[QUERY_TRANSPORT],
    },
    tripInformation: {
      kids,
      adults:
        kids.length > 0 && adults.length === 0
          ? Array(DEFAULT_ADULTS_NB).fill(DEFAULT_ADULT_VALUE)
          : adults,
    },
  };
}

function getSellerTripInformation() {
  // CMTA sellers
  const queryParams = getQueryParameters(windowWrapper.getLocation()) || {};
  const { [QUERY_CMTA]: cmtaQueryParam } = queryParams;
  if (typeof cmtaQueryParam !== 'undefined') {
    const { tripInformation } = stateFromQueryParams();
    return tripInformation;
  }
  // CXO sellers
  const cxoTravelers = getCxoTravelers() || [];

  if (cxoTravelers.length === 0) {
    return;
  }

  return {
    kids: parseCxoKids(cxoTravelers.filter(({ isChild }) => isChild)),
    adults: parseCxoAdults(cxoTravelers.filter(({ isChild }) => !isChild)),
  };
}

function cleanTripInfoKids(state: ReduxState): ReduxState {
  if (!state.tripInformation) return state;
  const { kids = [] } = state.tripInformation;
  const cleanedKids = kids.map(({ neolid, birthdate, childcareIncluded = false }) => ({
    neolid,
    birthdate,
    childcareIncluded,
  }));
  return { ...state, tripInformation: { ...state.tripInformation, kids: cleanedKids } };
}

export function getInitialReduxState(initialState: ReduxState, locale: string): ReduxState {
  let state = {
    ...initialState,
    ...stateFromLocalStorage(locale, {}),
    ...stateFromCookies(),
  };
  // NOTE: With merge() behavior, subvalues that are undefined won't override existing values
  // ie: merge({ a: 1, b: 2 }, { a: undefined, b: null }) returns { a: 1, b: null }
  state = merge({}, state, stateFromQueryParams());

  // for seller erase tripInformation
  state = {
    ...state,
    tripInformation: {
      ...state.tripInformation,
      ...getSellerTripInformation(),
    },
  };
  state = cleanTripInfoKids(state);
  state = omitBy(state, value => value === null);

  return state;
}
